/**
 * useSwipeGesture Hook
 * LAYER-SHELL: Mobile touch swipe detection
 *
 * Native touch event-based swipe detection for mobile drawer.
 * No external dependencies - uses touchstart/touchend events.
 */
import { useEffect, useCallback, useRef } from 'react';

interface SwipeConfig {
  /** Minimum swipe distance in pixels to trigger action (default: 50) */
  threshold?: number;
  /** Maximum zone from edge to start swipe in pixels (default: 20) */
  edgeZone?: number;
  /** Callback when swipe right from left edge detected */
  onSwipeRight?: () => void;
  /** Callback when swipe left detected (anywhere) */
  onSwipeLeft?: () => void;
  /** Whether swipe gestures are enabled */
  enabled?: boolean;
}

interface TouchState {
  startX: number;
  startY: number;
  startedFromEdge: boolean;
}

export const useSwipeGesture = ({
  threshold = 50,
  edgeZone = 20,
  onSwipeRight,
  onSwipeLeft,
  enabled = true,
}: SwipeConfig) => {
  const touchState = useRef<TouchState | null>(null);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!enabled) return;

    const touch = e.touches[0];
    const startedFromEdge = touch.clientX <= edgeZone;

    touchState.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      startedFromEdge,
    };
  }, [enabled, edgeZone]);

  const handleTouchEnd = useCallback((e: TouchEvent) => {
    if (!enabled || !touchState.current) return;

    const touch = e.changedTouches[0];
    const { startX, startY, startedFromEdge } = touchState.current;

    const deltaX = touch.clientX - startX;
    const deltaY = touch.clientY - startY;

    // Ensure horizontal swipe (not vertical scroll)
    const isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);

    if (isHorizontalSwipe && Math.abs(deltaX) >= threshold) {
      if (deltaX > 0 && startedFromEdge && onSwipeRight) {
        // Swipe right from left edge - open drawer
        onSwipeRight();
      } else if (deltaX < 0 && onSwipeLeft) {
        // Swipe left anywhere - close drawer
        onSwipeLeft();
      }
    }

    touchState.current = null;
  }, [enabled, threshold, onSwipeRight, onSwipeLeft]);

  useEffect(() => {
    if (!enabled) return;

    // Passive: true for better scroll performance
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enabled, handleTouchStart, handleTouchEnd]);
};

export default useSwipeGesture;
